import {
  BackgroundListType,
  BackgroundMappingType,
  Equipment,
  IFormBackground,
} from "@app/types/background.types";
import { errorAlert, successAlert } from "@app/utils/alerts";
import {
  deleteData,
  getData,
  postData,
  putData,
} from "@services/utils/formHelpers";
import { create } from "zustand";
import dayjs from "dayjs";
import { normalizeDate } from "@app/utils/functions";
import { TipoAnalisis } from "@app/types/genericTables.types";

export function filter(
  filters: IFormBackground,
  backgroundList: BackgroundListType[]
) {
  const {
    process,
    start_date,
    end_date,
    start_calculation_date,
    end_calculation_date,
    year,
    month,
    equipment,
    detector,
    filter_medium,
    instalation,
  } = filters;
  const idsEquipment = equipment?.map((e) => e.codigo);
  const idsDetector = detector?.map((e) => e.id);
  const idsInstallation = instalation?.map((e) => e.id);

  const filterDateStart = start_date
    ? normalizeDate(new Date(start_date))
    : null;
  const filterDateEnd = end_date ? normalizeDate(new Date(end_date)) : null;

  const filterCalculationDateStart = start_calculation_date
    ? normalizeDate(new Date(start_calculation_date))
    : null;
  const filterCalculationDateEnd = end_calculation_date
    ? normalizeDate(new Date(end_calculation_date))
    : null;

  return backgroundList.filter((background) => {
    const processFilter = process && process.value;
    const processMatch = processFilter
      ? background.type?.toLowerCase() === process.value.toLowerCase()
      : true;

    const equipmentFilter = idsEquipment && idsEquipment.length > 0;
    const equipmentMatch = equipmentFilter
      ? background.equipment &&
        idsEquipment.includes(background.equipment?.codigo)
      : true;

    const detectorFilter = idsDetector && idsDetector.length > 0;
    const detectorMatch = detectorFilter
      ? background.detector?.id !== undefined &&
        idsDetector.includes(background.detector.id)
      : true;

    const filterFilter =
      Array.isArray(filter_medium) && filter_medium.length > 0;
    const filterMatch = filterFilter
      ? filter_medium.some(
          (item) =>
            background.filter?.trim().toLowerCase() ===
            item.value.trim().toLowerCase()
        )
      : true;

    const yearFilter = year !== undefined;
    const yearMatch = yearFilter
      ? background.creation_date &&
        dayjs(background.creation_date).year() === year
      : true;

    const monthFilter = month !== undefined;
    const monthMatch = monthFilter
      ? background.creation_date &&
        dayjs(background.creation_date).month() + 1 === month
      : true;

    const creationDate = normalizeDate(new Date(background.creation_date));

    const dateRangeMatch =
      (!filterDateStart || creationDate >= filterDateStart) &&
      (!filterDateEnd || creationDate <= filterDateEnd);

    const calculationDate = normalizeDate(
      new Date(background.calculation_date)
    );

    const calculationDateRangeMatch =
      (!filterCalculationDateStart ||
        calculationDate >= filterCalculationDateStart) &&
      (!filterCalculationDateEnd ||
        calculationDate <= filterCalculationDateEnd);

    const matchesTypeUncertainty =
      idsInstallation && idsInstallation.length > 0
        ? background.instalation &&
          idsInstallation.includes(background.instalation?.id)
        : true;

    return (
      equipmentMatch &&
      detectorMatch &&
      processMatch &&
      filterMatch &&
      yearMatch &&
      monthMatch &&
      dateRangeMatch &&
      calculationDateRangeMatch &&
      matchesTypeUncertainty
    );
  });
}

type useBackgroundType = {
  fondosList: BackgroundListType[];
  loadingDeleteCalculation: boolean;
  fondosFiltered: BackgroundListType[];
  lastFondo: BackgroundMappingType | null;
  get: (id: BackgroundListType["id"]) => BackgroundListType | undefined;
  query: IFormBackground;
  setQuery: (query: IFormBackground) => void;
  fetchAll: () => void;
  fetchByInstalacionAndAnalysisType: (
    instalacionId: number,
    analysisType: string
  ) => void;
  fetchByTypeAndEquipment: (
    type: any,
    equipment: Equipment
  
  ) => void;
  fetchReferenceValues: (instalacionId: number, type: string) => Promise<any[]>;
  fetchReferenceCodeTypeValues: (id: string, code: string, type: string, equipment: string) => Promise<any[]>;
  loading: boolean;
  loadingFiltered: boolean;
  loadingSave: boolean;
  loadingReferenceValues: boolean;
  save: (data: BackgroundMappingType) => Promise<boolean>;
  filteredBackground: () => BackgroundListType[];
  delete: (id: number) => void;
  deleteFondoCalculation: (id: number) => Promise<void>;
};

export const useBackground = create<useBackgroundType>((set, get) => ({
  loading: false,
  loadingDeleteCalculation: false,
  loadingSave: false,
  loadingFiltered: false,
  loadingReferenceValues: false,
  fondosList: [],
  fondosFiltered: [],
  lastFondo: null,
  query: {},
  setQuery: (query) => set({ query }),
  filteredBackground: () => filter(get().query, get().fondosList),
  get: (id) => get().fondosList.find((fondo) => fondo.id === id),
  fetchAll: async () => {
    set({ loading: true });
    try {
      const endpoint = "/fondos";
      const { data: response } = await getData(endpoint);

      set(() => ({
        // fondosList: response,
        fondosList: response.sort(
          (a: BackgroundListType, b: BackgroundListType) =>
            (b.id as number) - (a.id as number)
        ),
        loading: false,
      }));
    } catch (error) {
      console.error("Error fetching useBackground:", error);
      set({ loading: false });
    }
  },
  fetchByInstalacionAndAnalysisType: async (
    instalacionId: number,
    analysisType: string
  ) => {
    set({ loadingFiltered: true });
    try {
      const endpoint = `/fondos/filtered?analysis_type=${analysisType}&instalation=${instalacionId}`;
      const { data: response } = await getData(endpoint);
      set(() => ({
        fondosFiltered: response,
        loadingFiltered: false,
      }));
    } catch (error) {
      console.error("Error fondosFiltered useBackground:", error);
      set({ loadingFiltered: false });
    }
  },
  fetchByTypeAndEquipment: async (
    type: any,
    equipment: Equipment
  ) => {
    set({ loadingFiltered: true });
    try {
      const endpoint = `/fondos/filteredTypeAndEquipment/${type.value}/${equipment.id}`;
      const { data: response } = await getData(endpoint);
      set(() => ({
        lastFondo: response,
        loadingFiltered: false,
      }));
    } catch (error) {
      console.error("Error fondosFiltered useBackground:", error);
      set({ loadingFiltered: false });
    }
  },
  fetchByEquipoAndAnalysisType: async (
    instalacionId: number,
    analysisType: string
  ) => {
    set({ loadingFiltered: true });
    try {
      const endpoint = `/fondos/filtered?analysis_type=${analysisType}&instalation=${instalacionId}`;
      const { data: response } = await getData(endpoint);
      set(() => ({
        fondosFiltered: response,
        loadingFiltered: false,
      }));
    } catch (error) {
      console.error("Error fondosFiltered useBackground:", error);
      set({ loadingFiltered: false });
    }
  },
  fetchReferenceValues: async (instalacionId, type) => {
    set({ loadingReferenceValues: true });
    try {
      const endpoint = "/fondos/filtered/" + instalacionId + "/" + type;
      const { data: response } = await getData(endpoint);
      return response;
    } catch (error) {
      console.error("Error fetching reference values:", error);
      set({ loadingReferenceValues: false });
    } finally {
      set({ loadingReferenceValues: false });
    }
  },
  fetchReferenceCodeTypeValues: async (id, code, type, equipment) => {
    set({ loadingReferenceValues: true });
    try {
      const endpoint = "/fondos/filteredCode/" + id + "/" + code + "/" + type + "/" + equipment;
      const { data: response } = await getData(endpoint);
      return response;
    } catch (error) {
      console.error("Error fetching reference values:", error);
      set({ loadingReferenceValues: false });
    } finally {
      set({ loadingReferenceValues: false });
    }
  },
  save: async (data: BackgroundMappingType) => {
    set({ loadingSave: true });
    let hasErrors = false;

    try {
      const { id, ...rest } = data;
      const isNew = !id;
      const endpoint = `/fondos/${isNew ? "create" : id + "/edit"}`;

      const response = isNew
        ? await postData(endpoint, data)
        : await putData(endpoint, { ...rest });

      hasErrors = !response.success;
      if (hasErrors) {
        errorAlert("Error al guardar el fondo");
      } else successAlert("Fondo guardado correctamente");

      return hasErrors;
    } catch (error) {
      errorAlert("Error al guardar el fondo");
      hasErrors = true;
      return hasErrors;
    } finally {
      set({ loadingSave: false });
      !hasErrors && get().fetchAll();
    }
  },
  delete: async (id: number) => {
    set({ loading: true });
    try {
      const endpoint = `/fondos/${id}`;
      await deleteData(endpoint);
      successAlert("Fondo eliminado correctamente");
      get().fetchAll();
    } catch (error: any) {
      if (
        error.message &&
        error.message.indexOf("a foreign key constraint fails") > 0
      )
        errorAlert(
          "No se puede eliminar el fondo porque está relacionado con una muestra creada"
        );
      else errorAlert("Error al eliminar el fondo");
    } finally {
      set({ loading: false });
    }
  },
  deleteFondoCalculation: async (id: number) => {
    set({ loadingDeleteCalculation: true });
    try {
      const endpoint = `/fondo_calculation/${id}`;
      await deleteData(endpoint);
    } catch (error) {
      errorAlert("Error al eliminar calculo");
    } finally {
      set({ loadingDeleteCalculation: false });
    }
  },
}));
